/**
 * Silnik szablonów dokumentów (docs/04 rozdz. 6). Jeden kod dla serwera (PDF) i aplikacji (podgląd),
 * docelowo także dla telefonu (protokoły).
 *
 * Składnia:
 *   {{klient.nazwa}}                 — pole (wartość z danych, znaki HTML są neutralizowane)
 *   {{#if umowa.zaliczka}} … {{else}} … {{/if}}  — sekcja warunkowa (można zagnieżdżać)
 *   {{tabela_pozycji}}               — blok HTML (tabela) generowany z danych
 *   {{firma.logo}}                   — logo firmy (obraz w treści, doc-images.ts)
 */

export interface RenderInput {
  fields: Record<string, string | number | boolean | null | undefined>;
  blocks: Record<string, string>;
}

export interface RenderResult {
  html: string;
  /** pola użyte w szablonie, których nie ma w danych */
  missing: string[];
}

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const truthy = (v: unknown) => v !== null && v !== undefined && v !== false && v !== '' && v !== 0 && v !== '0';

/** Edytor HTML zamyka bloki w akapitach (<p>{{blok}}</p>) — tabela nie może być w akapicie. */
/** Logo ({{firma.logo}}) zostaje w akapicie — wyrównanie akapitu ustawia jego położenie. */
function unwrapBlocks(html: string, blockNames: string[]): string {
  let out = html;
  for (const b of blockNames.filter((k) => k !== 'firma.logo')) out = out.replace(new RegExp(`<p[^>]*>\\s*\\{\\{\\s*${b}\\s*\\}\\}\\s*</p>`, 'g'), `{{${b}}}`);
  return out;
}

export function renderTemplate(template: string, input: RenderInput): RenderResult {
  const missing = new Set<string>();
  let html = unwrapBlocks(template, Object.keys(input.blocks));

  // sekcje warunkowe — od najgłębszych (bez zagnieżdżonego {{#if}} w środku)
  const ifRe = /\{\{\s*#if\s+([\w.]+)\s*\}\}((?:(?!\{\{\s*#if)[\s\S])*?)\{\{\s*\/if\s*\}\}/;
  for (let guard = 0; guard < 500; guard++) {
    const m = ifRe.exec(html);
    if (!m) break;
    const [whole, key, body] = m as unknown as [string, string, string];
    const [yes, no = ''] = body.split(/\{\{\s*else\s*\}\}/);
    if (!(key in input.fields)) missing.add(key);
    html = html.replace(whole, truthy(input.fields[key]) ? yes! : no);
  }

  html = html.replace(/([ \t]*)\{\{\s*([\w.]+)\s*\}\}/g, (m: string, space: string, key: string, at: number, all: string) => {
    if (key in input.blocks) return space + input.blocks[key]!;
    const v = input.fields[key];
    if (v === undefined) {
      missing.add(key);
      return `${space}<span class="brak-pola">[brak: ${escapeHtml(key)}]</span>`;
    }
    const text = escapeHtml(v === null ? '' : String(v));
    // puste pole tuż przed znakiem interpunkcyjnym („na rachunek Wykonawcy {{firma.rachunek}}.”) — bez wiszącej spacji: „…Wykonawcy.”
    if (!text && /^[.,;:)]/.test(all.slice(at + m.length))) return '';
    return space + text;
  });
  return { html, missing: [...missing] };
}

// ───────────── kwota słownie (umowy) ─────────────

const ONES = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
const TEENS = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
const TENS = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
const HUNDREDS = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];

/** Odmiana: 1 → forma 1, 2–4 (bez 12–14) → forma 2, pozostałe → forma 3. */
export function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const d = n % 10;
  const t = n % 100;
  return d >= 2 && d <= 4 && !(t >= 12 && t <= 14) ? few : many;
}

function triple(n: number): string {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts = [HUNDREDS[h]!];
  if (rest >= 10 && rest < 20) parts.push(TEENS[rest - 10]!);
  else parts.push(TENS[Math.floor(rest / 10)]!, ONES[rest % 10]!);
  return parts.filter(Boolean).join(' ');
}

/** Liczba całkowita słownie (do miliardów). */
export function numberInWords(n: number): string {
  if (n === 0) return 'zero';
  const scales: [number, string, string, string][] = [
    [1_000_000_000, 'miliard', 'miliardy', 'miliardów'],
    [1_000_000, 'milion', 'miliony', 'milionów'],
    [1000, 'tysiąc', 'tysiące', 'tysięcy'],
  ];
  const parts: string[] = [];
  let rest = Math.floor(Math.abs(n));
  for (const [size, one, few, many] of scales) {
    const count = Math.floor(rest / size);
    if (count > 0) {
      parts.push(count === 1 ? one : `${triple(count)} ${plural(count, one, few, many)}`);
      rest %= size;
    }
  }
  if (rest > 0) parts.push(triple(rest));
  return (n < 0 ? 'minus ' : '') + parts.join(' ');
}

/** Kwota słownie: „czterdzieści pięć tysięcy dziewięćset sześćdziesiąt cztery złote 80/100”. */
export function amountInWords(gr: number): string {
  const zl = Math.floor(gr / 100);
  const g = gr % 100;
  return `${numberInWords(zl)} ${plural(zl, 'złoty', 'złote', 'złotych')} ${String(g).padStart(2, '0')}/100`;
}
